import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { brokersApi, type BrokerConversionRequest } from "@/api/brokers";
import { brokerKeys } from "./brokerKeys";
import { brokerageKeys } from "@/hooks/brokerages/brokerageKeys";

/** The broker's outstanding deals — the per-deal absorb picklist source for the
 *  Junior conversion path. Only fetched when the caller enables it (Junior +
 *  the conversion modal is open); Independent conversions never absorb. */
export function useBrokerOutstandingDeals(brokerId: string, enabled: boolean) {
  return useQuery({
    queryKey: brokerKeys.outstandingDeals(brokerId),
    queryFn: () => brokersApi.outstandingDeals(brokerId),
    enabled: enabled && !!brokerId,
  });
}

/** Convert a broker between Independent and Junior (with covering firm + the
 *  per-deal absorb decisions). Invalidates the broker detail + roster reads,
 *  the outstanding-deals key, and the brokerages roster so the type badge,
 *  affiliated-partner rows, and firm production all refresh. */
export function useConvertBroker(brokerId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: BrokerConversionRequest) => brokersApi.convert(brokerId, body),
    onSuccess: () => {
      // Broker side — detail header type badge + the roster list "Type" column.
      void qc.invalidateQueries({ queryKey: brokerKeys.detail(brokerId) });
      void qc.invalidateQueries({ queryKey: brokerKeys.lists() });
      void qc.invalidateQueries({ queryKey: brokerKeys.outstandingDeals(brokerId) });
      // Brokerage side — the firm roster / production (affiliation changed).
      void qc.invalidateQueries({ queryKey: brokerageKeys.all });
    },
  });
}
